"use client";

import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Loader2, ImageIcon, Box } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GenerationModal } from "@/components/generation-modal";
import {
  getSwaps,
  get2dTo3d,
  getMagicBackgrounds,
  getPropertyVisualizations,
} from "@/services/generation.service";


import type { GenerationType } from "@/types/generation";

interface GenerationsListProps {
  type: GenerationType;
}

export function GenerationsList({ type }: GenerationsListProps) {
  const [generations, setGenerations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState<any | null>(null);

  const fetchGenerations = async () => {
    try {
      setLoading(true);
      let response;

      switch (type) {
        case "swap":
          response = await getSwaps(page);
          break;
        case "2d-to-3d":
          response = await get2dTo3d(page);
          break;
        case "magic-background":
          response = await getMagicBackgrounds(page);
          break;
        case "property-visualize":
          response = await getPropertyVisualizations(page);
          break;
        default:
          throw new Error("Invalid generation type");
      }

      setGenerations(response.data);
      setHasMore(response.pagination.page < response.pagination.pages);
      setError(null);
    } catch (err) {
      console.error(`Failed to fetch ${type} generations:`, err);
      setError("Failed to load generations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGenerations();
  }, [type, page]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-500 hover:bg-green-600";
      case "FAILED":
        return "destructive";
      case "PROCESSING":
        return "bg-blue-500 hover:bg-blue-600";
      default:
        return "secondary";
    }
  };

  if (loading && generations.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/50 bg-destructive/10">
        <CardContent className="flex flex-col items-center justify-center py-6 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button variant="outline" onClick={fetchGenerations}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (generations.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
          <ImageIcon className="h-12 w-12 mb-4 opacity-20" />
          <p>No generations found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {generations.map((gen) => (
            <Card
              key={gen.id}
              className="overflow-hidden flex flex-col cursor-pointer transition-all hover:shadow-md hover:ring-2 hover:ring-primary/20"
              onClick={() => setSelectedGeneration(gen)}
            >
              <CardHeader className="p-4 pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <Badge
                      className={getStatusColor(gen.status)}
                      variant={gen.status === "FAILED" ? "destructive" : "default"}
                    >
                      {gen.status}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(gen.createdAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-4 flex-1 flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      Input
                    </p>
                    <div className="aspect-square relative rounded-md overflow-hidden bg-muted">
                      {gen.inputImageUrls?.[0] ? (
                        <img
                          src={gen.inputImageUrls[0]}
                          alt="Input"
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      Output
                    </p>
                    <div className="aspect-square relative rounded-md overflow-hidden bg-muted">
                      {type === "2d-to-3d" ? (
                        <div className="flex flex-col items-center justify-center h-full gap-1.5">
                          {gen.outputUrls?.[0] ? (
                            <>
                              <Box className="h-8 w-8 text-muted-foreground/60" />
                              <span className="text-xs text-muted-foreground/80 font-medium">3D Model</span>
                              <span className="text-[10px] text-muted-foreground/50">Click to view</span>
                            </>
                          ) : gen.status === "COMPLETED" ? (
                            <span className="text-xs text-muted-foreground">No output</span>
                          ) : (
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
                          )}
                        </div>
                      ) : gen.outputUrls?.[0] ? (
                        <img
                          src={gen.outputUrls[0]}
                          alt="Output"
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                           {gen.status === "COMPLETED" ? (
                              <span className="text-xs text-muted-foreground">No output</span>
                           ) : (
                              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
                           )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {gen.errorMessage && (
                  <div className="bg-destructive/10 p-2 rounded text-xs text-destructive mt-auto">
                    Error: {gen.errorMessage}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pagination controls */}
        <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="flex items-center text-sm text-muted-foreground">
              Page {page}
            </span>
            <Button
               variant="outline"
               size="sm"
               onClick={() => setPage(p => p + 1)}
               disabled={!hasMore}
            >
              Next
            </Button>
        </div>
      </div>

      <GenerationModal
        generation={selectedGeneration}
        type={type}
        open={selectedGeneration !== null}
        onClose={() => setSelectedGeneration(null)}
      />
    </>
  );
}
